import posthog, { type PostHog } from 'posthog-js';

import { POSTHOG_HOST, POSTHOG_KEY } from '@/constants/env';

export type AnalyticsEventMap = {
  user_login_error: { error?: unknown; payload?: unknown };
  user_token_error: { error: unknown };
  symbol_select: Record<string, unknown>;
  ai_chat_score: Record<string, unknown>;
  ai_conversation: Record<string, unknown>;
  chart_download_button_click: Record<string, unknown>;
  datahub_enable_access_button_click: Record<string, unknown>;
  download_button_click: Record<string, unknown>;
  export_code_copied: Record<string, unknown>;
  export_download_button_click: Record<string, unknown>;
  export_selection_opened: { source: string };
  tab_click: Record<string, unknown>;
  table_row_click: Record<string, unknown>;
  feed_article_reading_time: Record<string, unknown>;
  monthly_recap_rated: Record<string, unknown>;
  premium_enquire_sent: Record<string, unknown>;
  gxgo_text_field_submitted: Record<string, unknown>;
  gxgo_onboarding_submitted: Record<string, unknown>;
  gxgo_register_market_interest: Record<string, unknown>;
  past_export_deleted: Record<string, unknown>;
  past_export_regenerated: Record<string, unknown>;
  trade_export_started: Record<string, unknown>;
  trade_export_completed: Record<string, unknown>;
  trade_export_failed: Record<string, unknown>;
  trade_export_regenerated: Record<string, unknown>;
  catalogue_filter: Record<string, unknown>;
  $pageview: Record<string, unknown>;
};

export function captureEvent<K extends keyof AnalyticsEventMap>(
  name: K,
  payload: AnalyticsEventMap[K]
): void {
  posthog.capture(name as string, payload as Record<string, unknown>);
}

export function setUser(properties: Record<string, unknown>): void {
  posthog.capture('$set', { $set: properties });
}

export function identify(distinctId: string, properties?: Record<string, unknown>): void {
  posthog.identify(distinctId, properties);
}

export function alias(newId: string, oldId: string): void {
  posthog.alias(newId, oldId);
}

export function reset(): void {
  posthog.reset();
}

export function init(): PostHog | null {
  if (!POSTHOG_KEY || !POSTHOG_HOST) return null;
  const params = new URLSearchParams(window.location.hash.slice(1));
  const distinctID = params.get('distinct_id') ?? undefined;
  const sessionID = params.get('session_id') ?? undefined;
  return posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true }
    },
    error_tracking: { captureExtensionExceptions: false },
    ...(distinctID || sessionID ? { bootstrap: { distinctID, sessionID } } : {})
  } as Parameters<typeof posthog.init>[1]);
}
